import { InputAdornment } from "@mui/material";
import { useAchievements } from "api/achievements";
import { NumberFormatInput } from "components/FormControls/NumberFormatInput";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { SelectInput } from "components/FormControls/Select/Select";
import { findConditionLabel } from "components/Layout/Campaign/Common/CampaignRules/Rule/Conditions/utils";
import { useMessage } from "hooks/intl";
import { useManageInfiniteScroll } from "hooks/useManageInfiniteScroll";
import { get } from "lodash";
import { useState } from "react";
import { AchievementCompletionType } from "types/segments";
import { formatNumberValue } from "utilities/numbers";
import { BoxElement } from "./BoxElement";
import { useStyles } from "./SegmentConditions.styles";
import { segmentConditions } from "./helpers";

export const initialValuesAchievementCompletion: AchievementCompletionType = {
  achievementId: "",
  operator: { code: "" },
  completionCount: 0,
  type: "achievement_completion_count",
};

export function AchievementCompletion({
  formField,
  values,
  status,
  readOnly,
  setFieldValue,
}: {
  formField: string;
  values: any;
  status: any;
  readOnly?: boolean;
  setFieldValue: (field: string, value: any) => void;
}) {
  const classes = useStyles();
  const value = get(values, formField);

  const { message } = useMessage();
  const [achievementSearch, setAchievementSearch] = useState<string>();
  const { useInfiniteAchievmentsList } = useAchievements();
  const {
    data: infiniteAchievementsList,
    size: achievementsSize,
    setSize: setAchievementsSize,
    isValidating,
  } = useInfiniteAchievmentsList({
    filters: achievementSearch
      ? [
          {
            condition: "like",
            query: "name",
            value: achievementSearch,
          },
        ]
      : [],
    order: "asc",
    sortKey: "name",
  });

  const {
    infiniteData: achievementsList,
    anyElementsToFetch: anyAchievementsToFetch,
  } = useManageInfiniteScroll(infiniteAchievementsList || []);

  const achievementsItems =
    achievementsList.map((achievement) => {
      return {
        id: achievement.achievementId,
        label: achievement.name,
      };
    }) || [];

  const getInputAdornmentValue = (value: number) =>
    value === 1 ? "time" : "times";

  const isCompletionCountRange = value.operator.code === "is_number_between";
  const achievementIdError = get(status, "achievementId");
  const completionCountError = get(status, "completionCount");
  const fromCompletionCountError = get(status, "fromCompletionCount");
  const toCompletionCountError = get(status, "toCompletionCount");

  return (
    <>
      {value.isConditionSaved || readOnly ? (
        <>
          <BoxElement
            content={
              achievementsItems.find((item) => item.id === value.achievementId)
                ?.label || value.achievementId
            }
            error={achievementIdError}
          />
          {message({
            id: findConditionLabel(value.operator.code),
          }).toLocaleLowerCase()}
          {isCompletionCountRange ? (
            <>
              {message({ id: "is_between" }).toLocaleLowerCase()}{" "}
              <BoxElement
                content={formatNumberValue(value?.fromCompletionCount, "")}
                error={fromCompletionCountError}
              />{" "}
              {message({ id: "and" }).toLocaleLowerCase()}{" "}
              <BoxElement
                content={formatNumberValue(value?.toCompletionCount, "")}
                error={toCompletionCountError}
              />
            </>
          ) : (
            <BoxElement
              content={formatNumberValue(value?.completionCount, "")}
              error={completionCountError}
            />
          )}
        </>
      ) : (
        <>
          <SelectInput
            displaySearch
            items={achievementsItems}
            label={
              <RequiredFieldLabel
                text={message({
                  id: "achievementName",
                })}
              />
            }
            name={`${formField}.achievementId`}
            data-test-id="achievement-completion-name"
            className={classes.input}
            asyncSearch={(searchedQuery) => setAchievementSearch(searchedQuery)}
            loadMoreElements={() =>
              !isValidating && setAchievementsSize(achievementsSize + 1)
            }
            anyElementsToFetch={anyAchievementsToFetch}
          />
          <SelectInput
            className={isCompletionCountRange ? "" : classes.input}
            data-test-id="achievement-completion-operator"
            width="100%"
            items={segmentConditions.map(
              ({ id, label }: { id: string; label: string }) => ({
                id: id,
                label: message({ id: label }),
              }),
            )}
            label={<RequiredFieldLabel text={message({ id: "condition" })} />}
            name={`${formField}.operator.code`}
            onChange={(_, value) => {
              if (value === "is_number_between") {
                setFieldValue(`${formField}.completionCount`, undefined);
                setFieldValue(`${formField}.fromCompletionCount`, 0);
                setFieldValue(`${formField}.toCompletionCount`, 0);
              } else {
                setFieldValue(`${formField}.completionCount`, 0);
                setFieldValue(`${formField}.fromCompletionCount`, undefined);
                setFieldValue(`${formField}.toCompletionCount`, undefined);
              }
            }}
          />
          {isCompletionCountRange && (
            <>
              <NumberFormatInput
                allowNegative={false}
                className={classes.input}
                data-test-id="completion-number-from"
                label={
                  <RequiredFieldLabel
                    text={message({
                      id: "completionNumberFrom",
                    })}
                  />
                }
                name={`${formField}.fromCompletionCount`}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {message({
                        id: getInputAdornmentValue(value.fromCompletionCount),
                      }).toLowerCase()}
                    </InputAdornment>
                  ),
                }}
              />
              <NumberFormatInput
                allowNegative={false}
                className={classes.input}
                data-test-id="completion-number-to"
                label={
                  <RequiredFieldLabel
                    text={message({
                      id: "completionNumberTo",
                    })}
                  />
                }
                name={`${formField}.toCompletionCount`}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {message({
                        id: getInputAdornmentValue(value.toCompletionCount),
                      }).toLowerCase()}
                    </InputAdornment>
                  ),
                }}
              />
            </>
          )}
          {!isCompletionCountRange && !!value.operator.code && (
            <NumberFormatInput
              allowNegative={false}
              className={classes.input}
              data-test-id="completion-number"
              label={
                <RequiredFieldLabel
                  text={message({
                    id: "completionNumber",
                  })}
                />
              }
              name={`${formField}.completionCount`}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {message({
                      id: getInputAdornmentValue(value.completionCount),
                    }).toLowerCase()}
                  </InputAdornment>
                ),
              }}
            />
          )}
        </>
      )}
    </>
  );
}
